import logger from '@/logger';
import { lockScroll, unlockScroll } from '@/utils/helpers';

const AEROSYNC_WIDGET_URL = 'https://cdn.sync.aero.inc/2.1.6/aerosync-widget.js';
const AEROSYNC_WIDGET_CONTAINER_ID = 'aerosync-widget-container';
const AEROSYNC_WIDGET_IFRAME_ID = 'aerosync-widget';
const WIDGET_READY_TIMEOUT_MS = 20000;
const SDK_LOAD_TIMEOUT_MS = 15000;
const WIDGET_DEFAULT_HEIGHT_PX = 720;
const WIDGET_DEFAULT_WIDTH_PX = 375;
const WIDGET_EDGE_GAP_PX = 12;
const WIDGET_EXIT_GAP_PX = 10;
const WIDGET_EXIT_HEIGHT_PX = 44;
const WIDGET_KEYBOARD_EDGE_GAP_PX = 4;
const WIDGET_KEYBOARD_MIN_INSET_PX = 150;
const WIDGET_KEYBOARD_SHELL_CLASS = 'bank-links__widget-shell--keyboard';
const WIDGET_SHEET_SHELL_CLASS = 'bank-links__widget-shell--sheet';
// Phones, in either orientation. There the provider fills the screen like any full-page form
// and the browser keeps the focused field above the keyboard itself; a floating card would
// have to be positioned from viewport numbers that phone browsers report inconsistently.
const WIDGET_SHEET_MEDIA_QUERY = '(max-width: 560px), (max-height: 560px)';
const WIDGET_OPEN_BODY_CLASS = 'aerosync-widget-open';

let sdkPromise = null;
let isPageScrollLocked = false;

function setWidgetOpenState(isOpen) {
  if (!document.body) return;
  if (isOpen) {
    document.body.classList.add(WIDGET_OPEN_BODY_CLASS);
    // The page behind the provider overlay must not move while it is open. The shared lock
    // is reference counted, so it nests inside the Quick Deposit modal's own lock; it is
    // only released here if it was taken here.
    if (!isPageScrollLocked) {
      lockScroll({ forceFixed: true });
      isPageScrollLocked = true;
    }
  } else {
    document.body.classList.remove(WIDGET_OPEN_BODY_CLASS);
    if (isPageScrollLocked) {
      unlockScroll();
      isPageScrollLocked = false;
    }
  }
}

function loadSdk() {
  if (window.aerosync && typeof window.aerosync.initWidget === 'function') {
    return Promise.resolve(window.aerosync);
  }
  if (!sdkPromise) {
    // Own this load rather than caching an invalid/unsettled shared script promise.
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      let settled = false;
      let timer = null;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        if (error) {
          if (script.parentNode) script.parentNode.removeChild(script);
          reject(error);
        } else {
          resolve(window.aerosync);
        }
      };
      script.type = 'text/javascript';
      script.src = AEROSYNC_WIDGET_URL;
      script.async = true;
      script.onload = () => finish(window.aerosync &&
        typeof window.aerosync.initWidget === 'function'
        ? null : new Error('Aerosync widget SDK is unavailable.'));
      script.onerror = () => finish(new Error('Aerosync widget SDK could not load.'));
      timer = setTimeout(() => finish(new Error('Aerosync widget SDK timed out.')),
        SDK_LOAD_TIMEOUT_MS);
      document.body.appendChild(script);
    })
      .catch((error) => {
        sdkPromise = null;
        throw error;
      });
  }
  return sdkPromise;
}

function normalizeSuccess(event) {
  const eventData = event && event.data ? event.data : event;
  return {
    connectionId: eventData && eventData.connectionId,
    userReference: eventData && eventData.aeroPassUserUuid,
  };
}

function getErrorCode(error) {
  const payload = typeof error === 'string'
    ? error
    : (error && (error.code || error.message));
  const match = String(payload || '').match(/\b(?:AC|AR)-\d+\b/i);
  return match ? match[0].toUpperCase() : 'UNKNOWN';
}

function isSheetLayout() {
  return !!window.matchMedia && window.matchMedia(WIDGET_SHEET_MEDIA_QUERY).matches;
}

// layoutHeight is the height of the fixed overlay the card is centered in. With a keyboard
// open iOS reports window.innerHeight as the visible height, not the layout height, so the
// overlay itself is measured whenever it exists.
function getWidgetDimensions(layoutHeight) {
  const layoutViewportHeight = layoutHeight || window.innerHeight;
  const visualViewport = window.visualViewport;
  const viewportHeight = Math.floor(visualViewport
    ? visualViewport.height
    : window.innerHeight);
  const viewportWidth = Math.floor(visualViewport
    ? visualViewport.width
    : window.innerWidth);
  const viewportTop = Math.floor(visualViewport
    ? visualViewport.offsetTop
    : 0);
  // A virtual keyboard shrinks the visual viewport but not the layout viewport. While
  // it is open the exit button is hidden and the gaps tighten so the little space left
  // goes to the provider's focused field; the provider's own close control remains.
  const isKeyboardOpen = !!visualViewport && visualViewport.scale <= 1.01 &&
    (layoutViewportHeight - viewportHeight) > WIDGET_KEYBOARD_MIN_INSET_PX;
  const edgeGap = isKeyboardOpen ? WIDGET_KEYBOARD_EDGE_GAP_PX : WIDGET_EDGE_GAP_PX;
  const exitSpace = isKeyboardOpen ? 0 : WIDGET_EXIT_HEIGHT_PX + WIDGET_EXIT_GAP_PX;
  const availableTop = viewportTop + edgeGap;
  const availableBottom = (viewportTop + viewportHeight) - edgeGap;
  const availableHeight = Math.max(1, availableBottom - availableTop);
  const frameHeight = Math.max(1, Math.min(
    WIDGET_DEFAULT_HEIGHT_PX,
    availableHeight - exitSpace,
  ));
  const frameWidth = Math.max(1, Math.min(
    WIDGET_DEFAULT_WIDTH_PX,
    viewportWidth - (WIDGET_EDGE_GAP_PX * 2),
  ));
  const availableCenter = availableTop + (availableHeight / 2);
  const layoutViewportCenter = layoutViewportHeight / 2;

  return {
    centerOffsetY: Math.round(availableCenter - layoutViewportCenter),
    frameHeight,
    frameWidth,
    isKeyboardOpen,
  };
}

function applyWidgetDimensions(shell, clip, iframe, isSheet) {
  shell.classList.toggle(WIDGET_SHEET_SHELL_CLASS, isSheet);
  if (isSheet) {
    shell.classList.remove(WIDGET_KEYBOARD_SHELL_CLASS);
    [shell, clip, iframe].forEach((element) => {
      element.style.removeProperty('transform');
      element.style.removeProperty('width');
      element.style.removeProperty('height');
    });
    return { isKeyboardOpen: false };
  }
  const container = shell.parentNode;
  const dimensions = getWidgetDimensions(
    container ? container.getBoundingClientRect().height : 0,
  );
  shell.classList.toggle(WIDGET_KEYBOARD_SHELL_CLASS, dimensions.isKeyboardOpen);
  shell.style.transform = `translateY(${dimensions.centerOffsetY}px)`;
  shell.style.width = `${dimensions.frameWidth}px`;
  clip.style.height = `${dimensions.frameHeight}px`;
  iframe.style.height = `${dimensions.frameHeight}px`;
  iframe.style.width = `${dimensions.frameWidth}px`;
  return dimensions;
}

function getViewportSignature() {
  const visualViewport = window.visualViewport;
  if (!visualViewport) return '';
  return [
    Math.round(visualViewport.offsetTop),
    Math.round(visualViewport.height),
    Math.round(visualViewport.width),
    window.innerHeight,
  ].join(':');
}

function addHostShell(elementId, onClose) {
  const target = document.getElementById(elementId);
  const container = target &&
    target.querySelector(`#${AEROSYNC_WIDGET_CONTAINER_ID}`);
  const iframe = container &&
    container.querySelector(`#${AEROSYNC_WIDGET_IFRAME_ID}`);
  if (!container || !iframe) return null;

  const shell = document.createElement('div');
  shell.className = 'bank-links__widget-shell';
  const clip = document.createElement('div');
  clip.className = 'bank-links__widget-clip';
  const exitButton = document.createElement('button');
  exitButton.className = 'bank-links__widget-exit';
  exitButton.type = 'button';
  const exitLabel = document.createElement('span');
  exitLabel.className = 'bank-links__widget-exit-label';
  exitLabel.textContent = 'Return to PredictIt';
  exitButton.appendChild(exitLabel);
  exitButton.addEventListener('click', onClose);

  container.insertBefore(shell, iframe);
  shell.appendChild(clip);
  clip.appendChild(iframe);
  shell.appendChild(exitButton);
  // The layout is chosen when the overlay opens and again only when the device is turned,
  // never because a keyboard changed the viewport in the middle of typing.
  let isSheet = isSheetLayout();
  applyWidgetDimensions(shell, clip, iframe, isSheet);

  // While a phone keyboard is open the visible area can be panned by a drag, and iOS only
  // reports that once the drag ends (and the number pad settles in stages). Following the
  // visible area every frame for as long as the keyboard is open keeps the shell in place
  // instead of drifting and snapping back.
  let trackingFrame = null;
  let viewportSignature = getViewportSignature();
  const trackViewport = () => {
    trackingFrame = null;
    if (!shell.parentNode) return;
    const signature = getViewportSignature();
    let isKeyboardOpen = shell.classList.contains(WIDGET_KEYBOARD_SHELL_CLASS);
    if (signature !== viewportSignature) {
      viewportSignature = signature;
      isKeyboardOpen = applyWidgetDimensions(shell, clip, iframe, isSheet).isKeyboardOpen;
    }
    if (isKeyboardOpen) trackingFrame = window.requestAnimationFrame(trackViewport);
  };
  const handleViewportChange = () => {
    if (!shell.parentNode) return;
    const dimensions = applyWidgetDimensions(shell, clip, iframe, isSheet);
    viewportSignature = getViewportSignature();
    if (dimensions.isKeyboardOpen && trackingFrame === null && window.requestAnimationFrame) {
      trackingFrame = window.requestAnimationFrame(trackViewport);
    }
  };
  // A drag that starts on the dimmed backdrop or the shell's own chrome would scroll or pan
  // what is behind the overlay. Touches inside the provider's frame never reach this handler.
  const handleOverlayTouchMove = (event) => {
    if (event.cancelable) event.preventDefault();
  };
  container.style.overscrollBehavior = 'contain';
  container.addEventListener('touchmove', handleOverlayTouchMove, { passive: false });
  const handleOrientationChange = () => {
    isSheet = isSheetLayout();
    handleViewportChange();
  };
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('orientationchange', handleOrientationChange);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportChange);
    // iOS pans the visual viewport to the focused field after the keyboard opens and
    // reports that as a scroll, not a resize; without it the shell sits off-screen.
    window.visualViewport.addEventListener('scroll', handleViewportChange);
  }

  return () => {
    if (trackingFrame !== null && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(trackingFrame);
    }
    trackingFrame = null;
    container.removeEventListener('touchmove', handleOverlayTouchMove);
    exitButton.removeEventListener('click', onClose);
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('orientationchange', handleOrientationChange);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleViewportChange);
      window.visualViewport.removeEventListener('scroll', handleViewportChange);
    }
  };
}

function removeInjectedWidget(elementId) {
  const target = document.getElementById(elementId);
  const container = target &&
    target.querySelector(`#${AEROSYNC_WIDGET_CONTAINER_ID}`);
  if (container && container.parentNode) {
    container.parentNode.removeChild(container);
  }
}

export default {
  load() {
    return loadSdk();
  },
  launch({
    elementId,
    session,
    onSuccess,
    onClose,
    onError,
    onReady,
    onDiagnostic,
  }) {
    return loadSdk().then((sdk) => {
      if (!sdk || !sdk.initWidget) {
        throw new Error('Aerosync widget SDK is unavailable.');
      }

      let controls = null;
      let isTerminal = false;
      let isReady = false;
      let readyTimer = null;
      let removeShellListener = null;
      let handleEscape = null;

      const clearLifecycle = () => {
        setWidgetOpenState(false);
        if (readyTimer) {
          clearTimeout(readyTimer);
          readyTimer = null;
        }
        if (handleEscape) {
          document.removeEventListener('keydown', handleEscape);
        }
        if (removeShellListener) {
          removeShellListener();
          removeShellListener = null;
        }
      };
      const markReady = (source) => {
        if (isTerminal || isReady) return;
        isReady = true;
        if (readyTimer) {
          clearTimeout(readyTimer);
          readyTimer = null;
        }
        if (onReady) onReady(source);
        if (onDiagnostic) onDiagnostic('widget_ready');
      };
      const cleanup = ({ notifyClose, source, callExit }) => {
        if (isTerminal) return;
        isTerminal = true;
        clearLifecycle();
        if (controls && callExit && typeof controls.exit === 'function') {
          try {
            controls.exit();
          } catch (error) {
            // The host cleanup below remains authoritative.
          }
        }
        if (controls && typeof controls.destroy === 'function') {
          try {
            controls.destroy();
          } catch (error) {
            // The host cleanup below remains authoritative.
          }
        }
        removeInjectedWidget(elementId);
        if (notifyClose && onDiagnostic) onDiagnostic('widget_exit');
        if (notifyClose && onClose) onClose({ source });
      };
      handleEscape = (event) => {
        if (event.key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          cleanup({ notifyClose: true, source: 'escape', callExit: true });
        }
      };
      const handleHostClose = () => cleanup({
        notifyClose: true,
        source: 'host',
        callExit: true,
      });
      const handleError = (error) => {
        if (isTerminal) return;
        logger.warn('Aerosync widget reported a recoverable error.', {
          code: getErrorCode(error),
        });
        if (onDiagnostic) onDiagnostic('widget_error', getErrorCode(error));
        if (onError) onError(error);
      };
      const handleLoadTimeout = () => {
        if (isTerminal) return;
        const error = { code: 'WIDGET_LOAD_TIMEOUT' };
        logger.warn('Aerosync widget did not become ready.', {
          code: error.code,
        });
        cleanup({ notifyClose: false, source: 'timeout', callExit: true });
        if (onDiagnostic) onDiagnostic('widget_error', error.code);
        if (onError) onError(error);
      };

      const initialDimensions = getWidgetDimensions();
      const configuration = {
        elementId,
        iframeTitle: 'Connect a bank account',
        environment: session.environment,
        token: session.sessionToken,
        aeroPassUserUuid: session.userReference,
        zIndex: '10020',
        style: {
          width: `${initialDimensions.frameWidth}px`,
          height: `${initialDimensions.frameHeight}px`,
          bgColor: '#000000',
          opacity: 0.66,
        },
        onEvent: () => {
          markReady('event');
          if (onDiagnostic) onDiagnostic('widget_event');
        },
        onLoad: () => markReady('load'),
        onSuccess: (event) => {
          if (!isTerminal && onDiagnostic) onDiagnostic('widget_success');
          if (!isTerminal && onSuccess) onSuccess(normalizeSuccess(event));
        },
        onClose: () => cleanup({
          notifyClose: true,
          source: 'provider',
          callExit: false,
        }),
        onError: handleError,
      };
      if (session.configurationId) {
        configuration.configurationId = session.configurationId;
      }
      if (session.additionalAuthenticationRequired) {
        configuration.handleMFA = true;
        configuration.jobId = session.jobReference;
        configuration.connectionId = session.connectionReference;
      }

      controls = sdk.initWidget(configuration);
      if (!controls || !controls.launch) {
        throw new Error('Aerosync widget controls are unavailable.');
      }
      controls.launch();
      if (onDiagnostic) onDiagnostic('widget_open');
      setWidgetOpenState(true);
      removeShellListener = addHostShell(elementId, handleHostClose);
      document.addEventListener('keydown', handleEscape);
      if (!isReady) {
        readyTimer = setTimeout(() => {
          handleLoadTimeout();
        }, WIDGET_READY_TIMEOUT_MS);
      }

      return {
        close: handleHostClose,
        exit: handleHostClose,
        destroy() {
          cleanup({ notifyClose: false, source: 'destroy', callExit: true });
        },
        isReady() {
          return isReady;
        },
      };
    });
  },
};



// WEBPACK FOOTER //
// ./src/components/wallet/bank-links/aerosync-widget-adapter.js